import { type AskPunttChatAtom } from "@mg/schemas/src/christo/askPuntt";
import { useTranslation } from "react-i18next";

/**
 * A rule card waits on the one person warhol drew it for (warhol spec
 * surbhi.kapoor/0100): live for them until they say something in the chat,
 * whatever colleagues send after it, and read-only for everyone else, naming
 * who it waits on. A form with no owner keeps the last-assistant-turn rule.
 */
export type QuestionFormState = {
  /** The form takes an answer from this viewer. */
  live: boolean;
  /** Who the form still waits on, for a viewer who is not them; "" for none. */
  waitsOn: string;
};

export function questionFormState(
  owner: { ownerUserId?: string; ownerName?: string },
  viewer: {
    /** The last-assistant-turn rule, which a form with no owner keeps. */
    interactive: boolean;
    /** Whether this viewer may send an answer at all. */
    canAnswer: boolean;
    viewerUserId?: string;
    /** Who sent a message after the one this form is on. */
    laterSenderIds?: readonly string[];
  },
): QuestionFormState {
  const ownerId = owner.ownerUserId;
  // why the last-turn rule when no viewer is named: a surface that names none cannot tell the owner from anyone
  if (!ownerId || !viewer.viewerUserId) {
    return { live: viewer.interactive, waitsOn: "" };
  }
  const answered = viewer.laterSenderIds?.includes(ownerId) ?? false;
  if (ownerId === viewer.viewerUserId) {
    return { live: viewer.canAnswer && !answered, waitsOn: "" };
  }
  return { live: false, waitsOn: answered ? "" : (owner.ownerName ?? "") };
}

type ThreadMessage = {
  role: string;
  userId?: string;
  content?: string;
  components?: readonly AskPunttChatAtom[];
};

/**
 * For each message, who sent a message after it, sorted, so a memoized row
 * re-renders only when that list changes. A user message with no author is
 * the chat owner's, as the feed reads it (`isFeedMessageFromViewer`).
 *
 * why a click on a colleague's card is not counted: warhol answers it as the
 * colleague's and leaves her own card waiting (`rule_confirm._others_card`),
 * so it must not lock her card here either.
 */
export function laterSendersByMessage(
  messages: readonly ThreadMessage[],
  chatOwnerId?: string,
): string[][] {
  const owners = optionOwners(messages);
  const later: string[][] = [];
  const seen = new Set<string>();
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    later[index] = Array.from(seen).sort();
    const message = messages[index];
    const sender =
      message.role === "user" ? (message.userId ?? chatOwnerId) : undefined;
    if (sender && !picksAnothersCard(message.content ?? "", sender, owners)) {
      seen.add(sender);
    }
  }
  return later;
}

const SELECTION = /\(selection: ([^)]+)\)/g;

function selectionsOf(content: string): string[] {
  return Array.from(content.matchAll(SELECTION)).map((found) =>
    found[1].trim(),
  );
}

/** Each option value the owned forms on one message offer, with the person it waits on. */
function ownedOptions(message: ThreadMessage): [string, string][] {
  const options: [string, string][] = [];
  for (const atom of message.components ?? []) {
    if (atom.componentId !== "ask-puntt.chat-atom.question-form") continue;
    const owner = atom.payload.ownerUserId;
    if (!owner) continue;
    for (const question of atom.payload.questions) {
      for (const option of question.options) {
        if (option.value) options.push([option.value, owner]);
      }
    }
  }
  return options;
}

/**
 * Everyone each option value waits on. why a set: a value carries the rule's
 * hash, not the person's (`understanding_hash`), so two people shown the same
 * rule are offered the same values.
 */
function optionOwners(
  messages: readonly ThreadMessage[],
): Map<string, Set<string>> {
  const owners = new Map<string, Set<string>>();
  for (const [value, owner] of messages.flatMap(ownedOptions)) {
    owners.set(value, (owners.get(value) ?? new Set<string>()).add(owner));
  }
  return owners;
}

/**
 * Whether a message only picks options on cards that wait on someone else.
 * why any card of hers counts: warhol reads a click as on her own card first
 * when a colleague's card offers the same value (`answer_rule_card`).
 */
function picksAnothersCard(
  content: string,
  sender: string,
  owners: Map<string, Set<string>>,
): boolean {
  const picked = selectionsOf(content).flatMap((value) => {
    const waitsOn = owners.get(value);
    return waitsOn ? [waitsOn] : [];
  });
  return picked.length > 0 && picked.every((waitsOn) => !waitsOn.has(sender));
}

/**
 * Whether `content`, sent by `sender`, answers the rule cards on `message`:
 * only a pick of one of their own options does, and only from the person the
 * card waits on; true for a message with none, where the header match stands.
 * why: in a shared chat every card has the same header, and two people shown
 * the same rule are offered the same values, so a click on a colleague's card
 * hid her own card as answered. With no sender named, the pick alone decides.
 */
export function answersOwnedForms(
  content: string,
  message: ThreadMessage | undefined,
  sender?: string,
): boolean {
  const options = message ? ownedOptions(message) : [];
  const picked = selectionsOf(content);
  return (
    options.length === 0 ||
    options.some(
      ([value, owner]) =>
        picked.includes(value) && (sender == null || owner === sender),
    )
  );
}

/** The note under a rule card that waits on someone else. */
export function FormWaitsOnOwnerNotice({
  testId,
  name,
}: {
  testId: string;
  name: string;
}) {
  const { t } = useTranslation("ui");
  if (!name) return null;
  return (
    <p className="mt-2 text-xs text-puntt-neutral-gray-11" data-testid={testId}>
      {t("askV2.transport.formWaitsOnOwner", { name })}
    </p>
  );
}
